import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, ButtonBase, Checkbox, Dialog, FormControlLabel, InputBase, makeStyles } from '@material-ui/core';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { AddIcon, CloseIcon, DropDownIcon, DropUpIcon } from '../../../../../components/Icon';
import { AutoCompleteSelect } from '../../../../../components';
import LongRemark from '../../../../../components/LongRemark';
import RobotImg from '../../../../../static/images/robot.png';
import { baseCssOccupieddischarge } from '../../../../BedStatus/components/PopWindows/baseCss';
import { classNames } from '../../../../../utils';
import { teleVisitAPI } from '../../../../../apis';
import { i18n } from '../../../../../language';
import DisconnectRobot from './DisconnectRobot';
import Counter from './Field/Counter';
import UpdateRobot from './UpdateRobot';
const useStyles = makeStyles(() => ({
  root: {
    '& .MuiDialog-paperWidthSm': {
      maxWidth: 'none',
    },
    '& .MuiPaper-root': {
      backgroundColor: '#FFFFFF',
      borderRadius: '15px !important',
      boxShadow: '0 -0.125rem 1.3125rem rgba(30, 56, 140, 0.18)',
      margin: '0',
      paddingTop: '2.722rem',
      width: '44.375rem',
      overflowY: 'hidden',
    },
  },
  title: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1.5rem',
    color: '#2e4469',
  },
  closeBtn: {
    width: '1.945rem',
    height: '1.945rem',
  },
  closeIcon: {
    fontSize: '1.5rem',
    color: '#538fff',
  },
  robot: {
    width: '4.563rem',
    minWidth: '4.563rem',
    height: '4.563rem',
    minHeight: '4.563rem',
    borderRadius: '8px',
    backgroundColor: '#fff',
    border: 'solid 1px #538fff',
  },
  registerBtn: {
    textAlign: 'center',
    borderRadius: '8px',
    backgroundColor: '#28d2ab',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#fff',
    width: '12.5rem',
    height: '2.875rem',
    '&.MuiButtonBase-root.Mui-disabled': {
      backgroundColor: '#e4e7eb',
      color: '#2e4469',
    },
  },
  disconnectBtn: {
    textAlign: 'center',
    borderRadius: '8px',
    backgroundColor: '#ff655c',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#fff',
    width: '12.5rem',
    height: '2.875rem',
    marginRight: '1.25rem',
    '&.MuiButtonBase-root.Mui-disabled': {
      backgroundColor: '#e4e7eb',
      color: '#2e4469',
    },
  },
  contentTitle: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    color: '#666666',
  },
  star: {
    fontSize: '0.875rem',
    color: '#ff0000',
  },
  input: {
    width: '100%',
    height: '3.5rem',
    borderRadius: '10px',
    border: 'solid 1px #e4e7eb',
    paddingLeft: '1.25rem',
    paddingRight: '1.188rem',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
    backgroundColor: '#fff',
    '&:disabled': {
      backgroundColor: '#f5f5f5 !important',
    },
    '&.Mui-error': {
      borderColor: '#f44336 !important',
    },
  },
  hours: {
    fontSize: '1rem',
    fontWeight: '500',
    color: '#2e4469',
    marginLeft: '0.75rem',
    fontFamily: 'NotoSans-Medium',
  },
  cancelingBooking: {
    fontSize: '0.875rem',
    color: '#666',
    fontWeight: '500',
    fontFamily: 'NotoSans-Medium',
    marginBottom: '0.625rem',
  },
  checkBox: {
    '& .MuiTypography-root': {
      fontSize: '0.875rem',
      color: '#7d86a9',
    },
  },
  deleteIcon: {
    fontSize: '1.25rem',
    color: '#525e6c',
  },
  dropIcon: {
    fontSize: '0.75rem',
    color: '#231f20',
  },
  iconBtn: {
    width: '1.5rem',
    height: '1.5rem',
  },
  addZoomBtn: {
    display: 'flex',
    color: '#538fff',
    fontSize: '1rem',
    fontFamily: 'NotoSans-Bold',
  },
  addIcon: {
    fontSize: '1.125rem',
    marginRight: '0.688rem',
  },
  errorMsg: {
    color: '#f44336',
    fontFamily: 'NotoSans-Medium',
  },
}));

const UPDATE_STATUS = {
  UPDATE: 'UPDATE',
  AVAILABLE_TO_UNAVAILABLE: 'AVAILABLE_TO_UNAVAILABLE',
  UNAVAILABLE_TO_AVAILABLE: 'UNAVAILABLE_TO_AVAILABLE',
};

const EditRobot = ({ onClose, editData, updateRobotApi, disconnectRobot }) => {
  const classes = useStyles();
  const baseClasses = baseCssOccupieddischarge();
  const [disconnectConfirmDialog, setDisconnectconfirmDialog] = useState(false);
  const [updateConfirmDialog, setUpdateConfirmDialog] = useState(false);
  const [dropdownData, setDropdownData] = useState(null);
  const [updateStatus, setUpdateStatus] = useState(UPDATE_STATUS.UPDATE);
  const [zoomKey, setZoomKey] = useState(editData?.zoomAccountMappingVOList?.length ?? 0);
  const language = useSelector((state) => state.settings.language);
  const [formData, setFormData] = useState({
    hospitalId: editData?.hospitalId,
    wardId: editData?.wardId,
    robotId: editData?.robotId,
    robotName: editData?.robotName,
    robotSerialNumber: editData?.robotSerialNumber,
    robotStatus: editData?.robotStatus,
    robotType: editData?.robotType,
    unavailableDurationTime: editData?.unavailableDurationTime ? editData?.unavailableDurationTime : 2,
    smsNotificationForUnavailable: editData?.smsNotificationForUnavailable ?? true,
    zoomAccountMappingVOList: editData?.zoomAccountMappingVOList
      ? _.map(editData.zoomAccountMappingVOList, (item, index) => ({ ...item, open: true, disabled: true, key: index }))
      : [],
    version: editData?.version,
  });
  const [errorMsg, setErrorMsg] = useState({});

  const checkItem = (item) => {
    if (typeof item === 'string') {
      return item.trim() === '';
    }
    return item === '';
  };

  const getFiledName = useCallback((key) => {
    let fileName = '';
    switch (key) {
      case 'robotName':
        fileName = i18n.t('TELEVISIT_SETTING_POPUP_ROBOT_NAME');
        break;
      case 'robotSerialNumber':
        fileName = i18n.t('TELEVISIT_SETTING_POPUP_SERIAL_NUMBER');
        break;
      case 'zoomAccountId':
        fileName = i18n.t('TELEVISIT_SETTING_POPUP_ACCOUNT_ID');
        break;
      case 'zoomClientId':
        fileName = i18n.t('TELEVISIT_SETTING_POPUP_CLIENT_ID');
        break;
      case 'zoomClientSecret':
        fileName = i18n.t('TELEVISIT_SETTING_POPUP_CLIENT_SECRET');
        break;
      default:
        break;
    }
    return fileName;
  }, []);
  const checkFormValue = useCallback((key, value, index) => {
    let indexV = index;
    if (index === undefined) {
      indexV = '';
    }

    if (checkItem(value)) {
      setErrorMsg((oldData) => {
        return { ...oldData, [key + indexV]: i18n.t('SETTING_IS_A_REQUIRED_FIELD', { value: getFiledName(key) }) };
      });
    } else {
      setErrorMsg((oldData) => {
        let tempData = { ...oldData };
        delete tempData[key + indexV];
        return { ...tempData };
      });
    }
  }, []);

  const formHandler = useCallback((key, index) => {
    if (key === 'robotStatus' || key === 'unavailableDurationTime' || key === 'robotType') {
      return (value) => {
        setFormData((preData) => ({ ...preData, [key]: value }));
      };
    } else if (key === 'smsNotificationForUnavailable') {
      return (value) => {
        setFormData((preData) => ({ ...preData, [key]: value.target.checked }));
      };
    } else if (
      key === 'zoomAccount' ||
      key === 'zoomAccountId' ||
      key === 'zoomClientId' ||
      key === 'zoomClientSecret'
    ) {
      return (event) => {
        setFormData((preData) => {
          let tempData = { ...preData };

          tempData.zoomAccountMappingVOList[index][key] = event.target.value;
          return tempData;
        });
      };
    } else {
      return (event) => {
        checkFormValue(key, event.target.value);
        setFormData((preData) => ({ ...preData, [key]: event.target.value }));
      };
    }
  }, []);

  const statusItemList = useMemo(() => {
    let list = [];
    _.map(dropdownData?.statusData, (item, index) => {
      if (item.dataKey === 'RobotStatus') {
        list.push({ text: language === 'en-us' ? item.descEN : item.descCH, value: Number(item.dataValue) });
      }
    });
    return list;
  }, [dropdownData, language]);

  const robotTypeItemList = useMemo(() => {
    let list = [];
    _.map(dropdownData?.staticData, (item, index) => {
      if (item.dataType === 'RobotType') {
        list.push({ text: language === 'en-us' ? item.descEN : item.descCH, value: item.dataValue });
      }
    });
    return list;
  }, [dropdownData, language]);

  const updateHandler = (props) => () => {
    if (editData.robotStatus !== formData.robotStatus) {
      if (editData.robotStatus === 1) {
        setUpdateStatus(UPDATE_STATUS.AVAILABLE_TO_UNAVAILABLE);
      } else {
        setUpdateStatus(UPDATE_STATUS.UNAVAILABLE_TO_AVAILABLE);
      }
    } else {
      setUpdateStatus(UPDATE_STATUS.UPDATE);
    }
    setUpdateConfirmDialog(true);
  };

  const updateApi = useCallback(() => {
    if (typeof updateRobotApi === 'function') {
      updateRobotApi({
        ...formData,
        robotSerialNumber: formData.robotSerialNumber.trim(),
        robotName: formData.robotName.trim(),
        zoomAccountMappingVOList: _.map(formData.zoomAccountMappingVOList, (item) => {
          let temp = {
            ...item,
            zoomAccountId: item.zoomAccountId.trim(),
            zoomClientId: item.zoomClientId.trim(),
            zoomClientSecret: item.zoomClientSecret.trim(),
          };
          delete temp.key;
          delete temp.open;
          delete temp.disabled;
          return { ...temp };
        }),
      });
    }
  }, [formData, updateRobotApi]);

  const disconnectHandler = (props) => () => {
    setDisconnectconfirmDialog(props);
  };

  const disconnectApi = () => {
    if (typeof disconnectRobot === 'function') {
      disconnectRobot(formData);
    }
  };

  const disable = useMemo(
    () =>
      _.some(formData, (item) => {
        if (typeof item === 'object') {
          return _.some(item, (zoomItem) => _.some(zoomItem, (zoomDataItem) => checkItem(zoomDataItem)));
        }
        return checkItem(item);
      }) || _.some(errorMsg, (item) => item),
    [formData, errorMsg]
  );
  // dropdown data
  const staticDataListApi = useCallback(async () => {
    try {
      const { result } = await teleVisitAPI.getStaticDataList();
      setDropdownData(result);
    } catch (error) {}
  }, []);

  useEffect(() => {
    (async () => {
      await staticDataListApi();
    })();
  }, [staticDataListApi]);

  const handleAddZoom = useCallback(() => {
    setFormData((oldData) => ({
      ...oldData,
      zoomAccountMappingVOList: [
        ...oldData.zoomAccountMappingVOList,
        {
          zoomAccount: '',
          zoomAccountId: '',
          zoomClientId: '',
          zoomClientSecret: '',
          zoomIsPaidAccount: false,
          open: true,
          key: zoomKey + 1,
        },
      ],
    }));
    setZoomKey((oldKey) => oldKey + 1);
  }, [zoomKey]);

  const handleOpenZoomDetail = useCallback(
    (open, index) => () => {
      setFormData((oldData) => {
        let tempData = { ...oldData };
        tempData.zoomAccountMappingVOList[index].open = open;
        return tempData;
      });
    },
    []
  );

  const checkEmail = useCallback(
    (value, index) => async () => {
      try {
        const schema = Yup.string()
          .email(i18n.t('TELEVISIT_SETTING_ROBOT_EMAIL_ERROR_MSG'))
          .required(i18n.t('TELEVISIT_SETTING_ROBOT_EMAIL_ERROR_MSG'));

        schema.validateSync(value);
        setErrorMsg((oldData) => ({
          ...oldData,
          [`zoomAccount${index}`]: '',
        }));
      } catch (error) {
        setErrorMsg((oldData) => ({
          ...oldData,
          [`zoomAccount${index}`]: error.message,
        }));
      }
    },
    []
  );

  return (
    <>
      {!disconnectConfirmDialog && !updateConfirmDialog && (
        <Dialog className={classes.root} open={true}>
          {/* header */}
          <Box display="flex" justifyContent="space-between" mb="1.965rem" p="0 1.875rem">
            <div className={classes.title}>{i18n.t('TELEVISIT_SETTING_POPUP_EDIT_ROBOT')}</div>
            <ButtonBase className={classes.closeBtn} onClick={onClose}>
              <CloseIcon className={classes.closeIcon} />
            </ButtonBase>
          </Box>
          {/* body */}
          <Box
            display="flex"
            justifyContent="space-between"
            gridColumnGap="1.25rem"
            p="0 1.875rem"
            style={{ overflowY: 'auto' }}
          >
            <div>
              <img src={RobotImg} className={classes.robot} alt="robot" />
            </div>
            <Box display="flex" flexDirection="column" gridGap="2rem" width="100%">
              <Box display="flex" flexDirection="column" gridGap="0.625rem">
                <div className={classes.contentTitle}>{i18n.t('TELEVISIT_SETTING_POPUP_SERIAL_NUMBER')}</div>
                <InputBase
                  className={classes.input}
                  value={formData.robotSerialNumber}
                  onChange={formHandler('robotSerialNumber')}
                  disabled={true}
                />
              </Box>
              <Box display="flex" flexDirection="column" gridGap="0.625rem">
                <div className={classes.contentTitle}>
                  {i18n.t('TELEVISIT_SETTING_POPUP_ROBOT_TYPE')}
                  <span className={classes.star}>*</span>
                </div>
                <AutoCompleteSelect
                  itemList={robotTypeItemList}
                  value={formData.robotType}
                  onChange={formHandler('robotType')}
                  disabled={true}
                />
              </Box>
              <Box display="flex" flexDirection="column" gridGap="0.625rem">
                <div className={classes.contentTitle}>
                  {i18n.t('TELEVISIT_SETTING_POPUP_ROBOT_NAME')}
                  <span className={classes.star}>*</span>
                </div>
                <InputBase
                  className={classes.input}
                  value={formData.robotName}
                  onChange={formHandler('robotName')}
                  inputProps={{ maxLength: 20 }}
                  error={Boolean(errorMsg[`robotName`])}
                />
                <div className={classes.errorMsg}>{errorMsg[`robotName`]}</div>
              </Box>
              <Box display="flex" flexDirection="column" gridGap="0.625rem">
                <div className={classes.contentTitle}>
                  {i18n.t('TELEVISIT_SETTING_POPUP_STATUS')}
                  <span className={classes.star}>*</span>
                </div>
                <AutoCompleteSelect
                  itemList={statusItemList}
                  value={formData.robotStatus}
                  onChange={formHandler('robotStatus')}
                />
              </Box>
              {formData.robotStatus === 0 && (
                <Box display="flex" flexDirection="column">
                  <Box display="flex">
                    <div className={classes.cancelingBooking}>{i18n.t('TELEVISIT_SETTING_POPUP_AUTO_CANCEL')}</div>
                    <LongRemark
                      content={i18n.t('TELEVISIT_SETTING_POPUP_AUTO_CANCEL_REMARK')}
                      width="100%"
                      minWidth="10rem"
                      id="longremark-zoom"
                    />
                  </Box>
                  <Box display="flex" alignItems="center">
                    <Counter
                      onChange={formHandler('unavailableDurationTime')}
                      value={formData.unavailableDurationTime}
                      width="15.781rem"
                    />
                    <div className={classes.hours}>{i18n.t('TELEVISIT_SETTING_POPUP_HOURS')}</div>
                  </Box>
                  <FormControlLabel
                    id="bed_occupieddischarge_home_leave"
                    control={<Checkbox />}
                    label={i18n.t('TELEVISIT_SETTING_POPUP_SEND_SMS')}
                    className={classNames(classes.checkBox, baseClasses.checkBox)}
                    checked={Boolean(formData.smsNotificationForUnavailable)}
                    onChange={formHandler('smsNotificationForUnavailable')}
                  />
                </Box>
              )}

              {formData.zoomAccountMappingVOList.length > 0 &&
                formData.zoomAccountMappingVOList.map((item, index) => (
                  <Box
                    display="flex"
                    flexDirection="column"
                    bgcolor="#f5faff"
                    border="solid 1px #538fff"
                    borderRadius="10px"
                    padding="1.25rem 0.75rem 1.25rem 1.875rem"
                    gridGap="2rem"
                    key={item.key}
                  >
                    <Box display="flex" justifyContent="space-between">
                      <Box display="flex" alignItems="center" mb="-0.312rem">
                        {`${i18n.t('TELEVISIT_SETTING_POPUP_ZOOM')} ${index + 1}`}
                        <LongRemark
                          content={i18n.t('TELEVISIT_SETTING_POPUP_ZOOM_REMARK')}
                          width="100%"
                          minWidth="10rem"
                          id="longremark-zoom"
                        />
                      </Box>
                      <Box display="flex" alignItems="center" mb="-0.312rem" gridGap="1.25rem">
                        <ButtonBase className={classes.iconBtn} onClick={handleOpenZoomDetail(!item.open, index)}>
                          {item.open ? (
                            <DropUpIcon className={classes.dropIcon} />
                          ) : (
                            <DropDownIcon className={classes.dropIcon} />
                          )}
                        </ButtonBase>
                      </Box>
                    </Box>
                    {item.open && (
                      <>
                        <Box display="flex" flexDirection="column" gridGap="0.625rem">
                          <div className={classes.contentTitle}>
                            {i18n.t('TELEVISIT_SETTING_POPUP_ZOOM_EMAIL')}
                            <span className={classes.star}>*</span>
                          </div>
                          <InputBase
                            className={classes.input}
                            value={item.zoomAccount}
                            onChange={formHandler('zoomAccount', index)}
                            disabled={item.disabled}
                            onBlur={() => {
                              (async () => {
                                await checkEmail(item.zoomAccount, index)();
                              })();
                            }}
                            error={errorMsg[`zoomAccount${index}`]}
                          />
                          <div className={classes.errorMsg}>{errorMsg[`zoomAccount${index}`]}</div>
                        </Box>
                        <Box display="flex" flexDirection="column" gridGap="0.625rem">
                          <div className={classes.contentTitle}>
                            {i18n.t('TELEVISIT_SETTING_POPUP_ACCOUNT_ID')}
                            <span className={classes.star}>*</span>
                          </div>
                          <InputBase
                            className={classes.input}
                            value={item.zoomAccountId}
                            onChange={formHandler('zoomAccountId', index)}
                            disabled={item.disabled}
                            onBlur={() => {
                              checkFormValue('zoomAccountId', item.zoomAccountId, item.key);
                            }}
                            error={Boolean(errorMsg[`zoomAccountId${item.key}`])}
                          />
                          <div className={classes.errorMsg}>{errorMsg[`zoomAccountId${item.key}`]}</div>
                        </Box>
                        <Box display="flex" flexDirection="column" gridGap="0.625rem">
                          <div className={classes.contentTitle}>
                            {i18n.t('TELEVISIT_SETTING_POPUP_CLIENT_ID')}
                            <span className={classes.star}>*</span>
                          </div>
                          <InputBase
                            className={classes.input}
                            value={item.zoomClientId}
                            onChange={formHandler('zoomClientId', index)}
                            disabled={item.disabled}
                            onBlur={() => {
                              checkFormValue('zoomClientId', item.zoomClientId, item.key);
                            }}
                            error={Boolean(errorMsg[`zoomClientId${item.key}`])}
                          />
                          <div className={classes.errorMsg}>{errorMsg[`zoomClientId${item.key}`]}</div>
                        </Box>
                        <Box display="flex" flexDirection="column" gridGap="0.625rem">
                          <div className={classes.contentTitle}>
                            {i18n.t('TELEVISIT_SETTING_POPUP_CLIENT_SECRET')}
                            <span className={classes.star}>*</span>
                          </div>
                          <InputBase
                            className={classes.input}
                            value={item.zoomClientSecret}
                            onChange={formHandler('zoomClientSecret', index)}
                            disabled={item.disabled}
                            onBlur={() => {
                              checkFormValue('zoomClientSecret', item.zoomClientSecret, item.key);
                            }}
                            error={Boolean(errorMsg[`zoomClientSecret${item.key}`])}
                          />
                          <div className={classes.errorMsg}>{errorMsg[`zoomClientSecret${item.key}`]}</div>
                        </Box>
                      </>
                    )}
                  </Box>
                ))}

              <Box display="flex">
                <ButtonBase className={classes.addZoomBtn} onClick={handleAddZoom}>
                  <AddIcon className={classes.addIcon} />
                  {i18n.t('TELEVISIT_SETTING_ROBOT_ADD_ZOOM_ACCOUNT')}
                </ButtonBase>
              </Box>
            </Box>
          </Box>
          {/* footer */}
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            p="1.125rem"
            mt="1.5rem"
            borderTop="solid 1px #e4e7eb"
          >
            <ButtonBase className={classes.disconnectBtn} onClick={disconnectHandler(true)}>
              {i18n.t('TELEVISIT_SETTING_POPUP_DISCONNECT')}
            </ButtonBase>
            <ButtonBase className={classes.registerBtn} onClick={updateHandler(true)} disabled={disable}>
              {i18n.t('TELEVISIT_SETTING_POPUP_UPDATE')}
            </ButtonBase>
          </Box>
        </Dialog>
      )}
      {disconnectConfirmDialog && <DisconnectRobot onClose={disconnectHandler(false)} disconnectApi={disconnectApi} />}
      {updateConfirmDialog && (
        <UpdateRobot onClose={() => setUpdateConfirmDialog(false)} updateApi={updateApi} updateStatus={updateStatus} />
      )}
    </>
  );
};

export { UPDATE_STATUS };

export default EditRobot;
